// @flow
import * as React from 'react';
import {
    List as VirtualizedList,
    ScrollSync,
    AutoSizer
} from 'react-virtualized';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { Box, Text, Columns, Column } from '@locus-taxy/neo-ui';
import HandleOutsideClick from 'common/components/core/util/HandleOutsideClick';
import { KeyEventHandler } from 'common/components/core/util/KeyEventsHandler';
import FpPlanContextMenu from './FpPlanContextMenu';
import { FpTimelineFactory } from './FpTimeline';
import FpTourEventComponent from './FpTourEventComponent';
import TimeUtils from 'common/utils/TimeUtils';
import theme from './FpPlanChartView.scss';
import Checkbox from 'common/components/core/checkbox';
import { Portal } from 'react-portal';

import FpBeatTimelineHelper from './FpBeatTimelineHelper';
import FpTimelineLeftCard from './FpTimelineLeftCard';

const EnhancedPlanContextMenu = HandleOutsideClick(
    KeyEventHandler(FpPlanContextMenu)
);

const FpPlanTimeline = FpTimelineFactory(
    FpTourEventComponent,
    EnhancedPlanContextMenu
);

const HEADER_SIZE = 30; //px

export default class FpPlanChartView extends React.Component {
    listRef = ref => {
        this.fpTourList = ref;
    };

    computeMinMaxTimeForEvents(eventGroups) {
        let minTime = Number.MAX_VALUE;
        let maxTime = 0;
        eventGroups.forEach(group => {
            const events = group.get('events');
            if (events.size > 0) {
                const operationShiftEvent = events.first();
                const shiftStartTime = operationShiftEvent.get('startTime');
                const shiftEndTime = operationShiftEvent.get('endTime');
                if (minTime > shiftStartTime) {
                    minTime = shiftStartTime;
                }
                const endTime = events.last().get('endTime');
                if (maxTime < endTime) {
                    maxTime = endTime;
                }
                if (maxTime < shiftEndTime) {
                    maxTime = shiftEndTime;
                }
            }
        });
        return {
            minTime,
            maxTime
        };
    }

    onVisitSelect = (beatId, visitId) => {
        const { onVisitClick } = this.props;
        onVisitClick(beatId, visitId);
    };

    setHoveredVisitOut = () => {
        const { onVisitHoverOutside } = this.props;
        onVisitHoverOutside();
    };

    setHoveredVisit = (beatId, visitId) => {
        const { onVisitHover } = this.props;
        onVisitHover(beatId, visitId);
    };

    onVisitHoverOut = debounce(() => {
        this.hoverActive = false;
        this.setHoveredVisitOut();
    }, this.HOVER_DELAY_TIME);

    onVisitHover = debounce((beatId, visitId) => {
        this.hoverActive = true;
        this.setHoveredVisit(beatId, visitId);
    }, this.HOVER_DELAY_TIME);

    getEmptyContainer = () => {
        return this.emptyContainer;
    };

    renderEmptyRow = (
        eventGroup: Map<string, string>,
        style: { height?: number, top?: number },
        scrollTop: number
    ) => {
        const { t, beats } = this.props;
        const leftOffset = 0;
        const topOffset = -14;
        const emptyStyle = {
            left: leftOffset,
            top: topOffset + style.top - scrollTop,
            height: style.height
        };
        const beat = beats?.find(beat => beat.beatId === eventGroup.get('id'));
        const { visits } = beat;
        const firstVisit = visits[0].eta.startTime;
        const lastVisit = visits[visits.length - 1].eta.completedTime;
        // TODO: not able to check this depricated
        const operatingHours = TimeUtils.formatTimeRange(
            firstVisit,
            lastVisit,
            firstVisit
        );
        return (
            <Portal node={this.getEmptyContainer()}>
                <Box
                    sx={emptyStyle}
                    className={classnames(theme.emptyRow, {
                        [theme.isSelected]: eventGroup.get('isSelected'),
                        [theme.isChecked]: eventGroup.get('isChecked')
                    })}
                >
                    <Box className={theme.emptyBox}>
                        <span className={classnames(theme.title, theme.bold)}>
                            {t('noTasksAllocated')}
                        </span>
                        {firstVisit ? (
                            <span className={theme.subtitle}>
                                {t('operatingHoursLabel', { operatingHours })}
                            </span>
                        ) : null}
                    </Box>
                </Box>
            </Portal>
        );
    };

    renderLeftColumn = ({ index, style }) => {
        const {
            visibleBeats,
            beatSelectionMap,
            toggleBeatSelection,
            onScroll,
            ...rest
        } = this.props;
        const beat = visibleBeats[index];
        const isBeatSelected = beatSelectionMap.get(beat.beatId);
        return (
            <FpTimelineLeftCard
                {...rest}
                onScroll={onScroll}
                theme={theme}
                style={style}
                beat={beat}
                index={index}
                isBeatSelected={isBeatSelected}
                toggleBeatSelection={toggleBeatSelection}
            />
        );
    };

    render() {
        const {
            selectedTourIds,
            zoomLevel,
            visitIdMap,
            visibleBeats,
            currentHoveredVisit,
            selectedVisit,
            selectedBeat,
            hoveredBeat,
            beatSelectionMap,
            onSelectAllToggle,
            isAllVisibleBeatsSelected,
            t,
            ...rest
        } = this.props;
        const rowHeight = 74;
        const leftWidth = 300;
        let groups;
        let focusTime;

        let minTime;
        let maxTime;

        if (!visibleBeats.length) {
            return (
                <Box p={2}>
                    <Text fontSize={3} fontWeight={600}>
                        {t('fpPlanView.noBeatsFound')}
                    </Text>
                </Box>
            );
        }
        if (visibleBeats.length) {
            ({ groups } = FpBeatTimelineHelper.makeEventGroups(
                visibleBeats,
                selectedTourIds,
                visitIdMap,
                currentHoveredVisit,
                hoveredBeat,
                selectedVisit,
                selectedBeat,
                beatSelectionMap
            ));
            ({ minTime, maxTime } = this.computeMinMaxTimeForEvents(groups));
        }
        return (
            <Box className={theme.autoSizerWrap} height="fluid">
                <AutoSizer>
                    {({ height, width }) => {
                        return (
                            <Box
                                className={theme.chartAndTasksWrap}
                                sx={{ height, width }}
                            >
                                <Box className={theme.chartContainer}>
                                    <ScrollSync>
                                        {({
                                            onScroll,
                                            scrollTop
                                        }: {
                                            onScroll: () => void,
                                            scrollTop: number
                                        }) => {
                                            return (
                                                <Box
                                                    className={
                                                        theme.tourListWrap
                                                    }
                                                    sx={{ height, width }}
                                                >
                                                    <div
                                                        className={
                                                            theme.scrollHack
                                                        }
                                                    />
                                                    <Columns height="fluid">
                                                        <Column
                                                            width="content"
                                                            classname={
                                                                theme.tourList
                                                            }
                                                        >
                                                            <Box
                                                                p={1}
                                                                height="30px"
                                                                className={
                                                                    theme.timelineHeading
                                                                }
                                                            >
                                                                <Columns
                                                                    height="fluid"
                                                                    space="small"
                                                                    alignY="center"
                                                                    align="center"
                                                                >
                                                                    <Column width="content">
                                                                        <Checkbox
                                                                            smallCheckbox
                                                                            checked={
                                                                                isAllVisibleBeatsSelected
                                                                            }
                                                                            theme={
                                                                                theme
                                                                            }
                                                                            onChange={
                                                                                onSelectAllToggle
                                                                            }
                                                                        />
                                                                    </Column>
                                                                    <Column width="fluid">
                                                                        <Columns align="center">
                                                                            <Text
                                                                                fontSize={
                                                                                    2
                                                                                }
                                                                                fontWeight={
                                                                                    600
                                                                                }
                                                                            >
                                                                                {t(
                                                                                    // eslint-disable-next-line max-len
                                                                                    'entityManagement:fpPlanView.timeline'
                                                                                )}
                                                                            </Text>
                                                                        </Columns>
                                                                    </Column>
                                                                </Columns>
                                                            </Box>
                                                            <VirtualizedList
                                                                ref={
                                                                    this.listRef
                                                                }
                                                                tabIndex={-1}
                                                                className={
                                                                    theme.tourListContainer
                                                                }
                                                                width={
                                                                    leftWidth
                                                                }
                                                                height={
                                                                    height -
                                                                    HEADER_SIZE
                                                                }
                                                                rowCount={
                                                                    visibleBeats.length
                                                                }
                                                                rowHeight={
                                                                    rowHeight
                                                                }
                                                                scrollTop={
                                                                    scrollTop
                                                                }
                                                                onScroll={
                                                                    onScroll
                                                                }
                                                                style={{
                                                                    overflow:
                                                                        'hidden'
                                                                }}
                                                                rowRenderer={
                                                                    this
                                                                        .renderLeftColumn
                                                                }
                                                            />
                                                            />
                                                        </Column>
                                                        <Column width="fluid">
                                                            <FpPlanTimeline
                                                                {...rest}
                                                                planStatus={
                                                                    this.props
                                                                        .planStatus
                                                                }
                                                                onMouseOver={
                                                                    this
                                                                        .onVisitHover
                                                                }
                                                                onMouseOut={
                                                                    this
                                                                        .onVisitHoverOut
                                                                }
                                                                onVisitSelect={
                                                                    this
                                                                        .onVisitSelect
                                                                }
                                                                emptyRowRenderer={
                                                                    this
                                                                        .renderEmptyRow
                                                                }
                                                                rowHeight={
                                                                    rowHeight
                                                                }
                                                                width={
                                                                    width -
                                                                    leftWidth
                                                                }
                                                                height={height}
                                                                onScroll={
                                                                    onScroll
                                                                }
                                                                focusTime={
                                                                    focusTime
                                                                }
                                                                visibleTimeStart={
                                                                    minTime
                                                                }
                                                                visibleTimeEnd={
                                                                    maxTime
                                                                }
                                                                eventGroups={
                                                                    groups
                                                                }
                                                                zoomLevel={
                                                                    zoomLevel
                                                                }
                                                            />
                                                        </Column>
                                                    </Columns>
                                                </Box>
                                            );
                                        }}
                                    </ScrollSync>
                                </Box>
                            </Box>
                        );
                    }}
                </AutoSizer>
            </Box>
        );
    }
}
